import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { t } from '@/lib/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught error:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="flex max-w-md flex-col items-center rounded-2xl border bg-card p-8 text-center shadow-sm">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <h1 className="mb-2 text-xl font-semibold">{t('common.errorBoundary')}</h1>
            <p className="mb-6 text-sm text-muted-foreground">{t('common.errorBoundaryDesc')}</p>
            {this.state.error?.message ? (
              <pre className="mb-4 max-w-full overflow-auto rounded bg-muted px-3 py-2 text-xs text-muted-foreground">
                {this.state.error.message}
              </pre>
            ) : null}
            <Button onClick={this.handleReload}>{t('action.reload')}</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
